import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MIDASProject } from '../../types/project.types';
import { DataSet, Report, DerivedDataSet, DatasetId, TabState, LayoutState, PaneNode, TabContainer, SplitPane, DropZone, WorkspaceId, createPaneId, createTabId } from '../../types';
import { Workspace, CreateWorkspaceOptions } from '../../types/workspace.types';
import { markAsTransient, clearTransient } from '../../types/serialization.types';
import logger from '../utils/logger';
import type { TableReference } from '../utils/duckdb';
import { updateDependentSQLQueries } from '../utils/sql/updateDependentSQLQueries';

/**
 * テーブル参照を抽出できるDuckDBインターフェース
 */
interface DuckDBTableReferenceExtractor {
  extractTableReferencesWithLocation(query: string): Promise<TableReference[]>;
}

interface ProjectStore {
  // 状態
  project: MIDASProject | null;

  // アクション
  loadProject: (project: MIDASProject) => void;
  updateProject: (updater: (project: MIDASProject) => MIDASProject) => void;
  addDataset: (dataset: DataSet) => void;
  updateDataset: (datasetId: DatasetId, updater: (dataset: DataSet) => DataSet) => void;
  updateDatasetName: (datasetId: DatasetId, newName: string, duckdb: DuckDBTableReferenceExtractor) => Promise<void>;
  removeDataset: (datasetId: DatasetId) => void;
  addDerivedDataset: (dataset: DerivedDataSet) => void;
  setProjectUpdateCallback: (callback: (project: MIDASProject) => void) => void;

  // ユーティリティメソッド
  getDataset: (datasetId: string) => DataSet | undefined;
  getAllDatasets: () => DataSet[];

  // 計算プロパティ
  datasets: Record<string, DataSet>;
  reports: Report[];

  // ===== ワークスペース操作 =====
  // ワークスペース取得
  getCurrentWorkspace: () => Workspace | null;
  getWorkspaceById: (workspaceId: WorkspaceId) => Workspace | null;

  // ワークスペース操作
  createWorkspace: (options: CreateWorkspaceOptions) => WorkspaceId;
  deleteWorkspace: (workspaceId: WorkspaceId) => void;
  renameWorkspace: (workspaceId: WorkspaceId, newName: string) => void;
  switchWorkspace: (workspaceId: WorkspaceId) => void;
  moveWorkspace: (workspaceId: WorkspaceId, direction: 'left' | 'right') => void;
  clearWorkspaceContext: (workspaceId: WorkspaceId) => void;

  // ===== レイアウト操作（現在のワークスペースに対して） =====
  // 現在のレイアウトを取得（派生状態）
  currentLayout: LayoutState;

  // レイアウト基本操作
  setLayout: (layout: LayoutState) => void;

  // タブ操作
  addTab: (containerId: string, tab: TabState, options?: { activatePane?: boolean }) => void;
  closeTab: (containerId: string, tabId: string) => void;
  activateTab: (containerId: string, tabId: string) => void;
  updateTabState: (containerId: string, tabId: string, updater: (tab: TabState) => TabState) => void;
  updateTabTitle: (tabId: string, newTitle: string) => void;

  // タブ移動
  moveTab: (tabId: string, fromContainerId: string, toContainerId: string, targetIndex?: number) => void;
  moveTabWithinPane: (containerId: string, fromIndex: number, toIndex: number) => void;

  // ペイン操作
  splitPane: (targetPaneId: string, direction: 'horizontal' | 'vertical', position: 'before' | 'after', newPane?: TabContainer) => string;
  closePane: (paneId: string) => void;
  updateSplitRatio: (splitPaneId: string, ratio: number) => void;

  // ユーティリティ
  findContainer: (containerId: string) => TabContainer | undefined;
  findTab: (tabId: string) => { container: TabContainer; tab: TabState } | undefined;
  getActiveContainer: () => TabContainer | undefined;
  handleDropZone: (dropZone: DropZone, draggedTabId: string, draggedFromPaneId: string) => void;

  // 内部メソッド
  _notifyProjectUpdate: () => void;
}

// プロジェクト更新のコールバック（メインプロセスへの通知用）
let projectUpdateCallback: ((project: MIDASProject) => void) | undefined;

export const setProjectUpdateCallback = (callback: (project: MIDASProject) => void) => {
  projectUpdateCallback = callback;
};

// ===== ヘルパー関数 =====

// デフォルトレイアウトを作成
const createDefaultLayout = (): LayoutState => ({
  rootPane: {
    type: 'container',
    id: createPaneId(`pane-${crypto.randomUUID()}`),
    tabs: [],
    activeTabId: createTabId(''),
  } as TabContainer,
  activeContainerId: undefined,
});

// ノードを再帰的に探索してコンテナを見つける
function findContainerInNode(node: PaneNode, containerId: string): TabContainer | undefined {
  if (node.type === 'container' && node.id === containerId) {
    return node;
  }
  if (node.type === 'split') {
    return findContainerInNode(node.leftChild, containerId) ||
           findContainerInNode(node.rightChild, containerId);
  }
  return undefined;
}

// 最初のコンテナIDを見つける
function findFirstContainerId(node: PaneNode): string | undefined {
  if (node.type === 'container') {
    return node.id;
  }
  if (node.type === 'split') {
    return findFirstContainerId(node.leftChild) || findFirstContainerId(node.rightChild);
  }
  return undefined;
}

// タブを探す
function findTabInLayout(rootNode: PaneNode, tabId: string): { container: TabContainer; tab: TabState } | undefined {
  const searchNode = (node: PaneNode): { container: TabContainer; tab: TabState } | undefined => {
    if (node.type === 'container') {
      const tab = node.tabs.find(t => t.id === tabId);
      if (tab) {
        return { container: node, tab };
      }
    } else if (node.type === 'split') {
      return searchNode(node.leftChild) || searchNode(node.rightChild);
    }
    return undefined;
  };
  return searchNode(rootNode);
}

// 空のペインを削除して兄弟を昇格
function cleanupEmptyPane(node: PaneNode, emptyPaneId: string): PaneNode {
  if (node.type === 'split') {
    if (node.leftChild.id === emptyPaneId) return node.rightChild;
    if (node.rightChild.id === emptyPaneId) return node.leftChild;

    const cleanedLeft = cleanupEmptyPane(node.leftChild, emptyPaneId);
    const cleanedRight = cleanupEmptyPane(node.rightChild, emptyPaneId);

    if (cleanedLeft !== node.leftChild || cleanedRight !== node.rightChild) {
      return { ...node, leftChild: cleanedLeft, rightChild: cleanedRight };
    }
  }
  return node;
}

export const useProjectStore = create<ProjectStore>()(
  devtools(
    (set, get) => ({
        // 初期状態
        project: null,
        
        // 計算プロパティ
        datasets: {},
        reports: [],
        
        // プロジェクトを読み込む
        loadProject: (project) => {
          logger.assert(project != null, 'Project must not be null');
          logger.assert(project.id != null && project.id.length > 0, 'Project ID must not be empty');
          
          logger.info('[ProjectStore] Loading project', {
            projectId: project.id,
            projectName: project.projectName,
            datasetCount: Object.keys(project.datasets || {}).length,
            reportCount: (project.reports || []).length,
            workspaceCount: (project.workspaces || []).length
          });
          
          set({ 
            project,
            datasets: project.datasets || {},
            reports: project.reports || []
          });
        },
        
        // プロジェクトを更新
        updateProject: (updater) => {
          set((state) => {
            if (!state.project) return state;
            
            const updatedProject = updater(state.project);
            
            // メインプロセスに通知
            if (projectUpdateCallback) {
              projectUpdateCallback(updatedProject);
            }
            
            return { 
              project: updatedProject,
              datasets: updatedProject.datasets || {},
              reports: updatedProject.reports || []
            };
          });
        },
        
        // データセットを追加
        addDataset: (dataset) => {
          logger.assert(dataset != null, 'Dataset must not be null');
          logger.assert(dataset.id != null && dataset.id.length > 0, 'Dataset ID must not be empty');

          const state = get();

          // 名前の重複チェック
          const existingDatasets = Object.values(state.datasets);
          const duplicateName = existingDatasets.find(d => d.name === dataset.name);
          if (duplicateName) {
            throw new Error(`Dataset with name "${dataset.name}" already exists (ID: ${duplicateName.id})`);
          }

          logger.info('[ProjectStore] Adding dataset', {
            datasetId: dataset.id,
            datasetName: dataset.name,
            datasetType: dataset.type,
            existingDatasetCount: Object.keys(state.datasets).length
          });

          state.updateProject((proj) => ({
            ...proj,
            datasets: {
              ...proj.datasets,
              [dataset.id]: dataset
            }
          }));
        },
        
        // データセットを更新
        updateDataset: (datasetId, updater) => {
          get().updateProject((proj) => {
            const dataset = proj.datasets[datasetId];
            if (!dataset) {
              logger.warn('[ProjectStore] Dataset not found:', datasetId);
              return proj;
            }

            // 更新後のデータセットを取得
            const updatedDataset = updater(dataset);

            // 名前が変更された場合は重複チェック
            if (updatedDataset.name !== dataset.name) {
              const existingDatasets = Object.entries(proj.datasets)
                .filter(([id]) => id !== datasetId); // 自分自身は除外
              const duplicateName = existingDatasets.find(([_, d]) => d.name === updatedDataset.name);

              if (duplicateName) {
                throw new Error(`Dataset with name "${updatedDataset.name}" already exists (ID: ${duplicateName[0]})`);
              }

              logger.info('[ProjectStore] Updating dataset name', {
                datasetId,
                oldName: dataset.name,
                newName: updatedDataset.name
              });
            }

            // DerivedDataSetのmaterializedプロパティに応じてtransientマーカーを更新
            if (updatedDataset.type === 'derived') {
              if (updatedDataset.materialized) {
                // materialized === true: dataをMDSに保存する（transientマーカーを削除）
                clearTransient(updatedDataset);
              } else {
                // materialized !== true: dataをMDSに保存しない（transientマーカーを設定）
                markAsTransient(updatedDataset, ['data']);
              }
            }

            return {
              ...proj,
              datasets: {
                ...proj.datasets,
                [datasetId]: updatedDataset
              }
            };
          });
        },

        // データセット名を変更（依存するSQLクエリも更新）
        updateDatasetName: async (datasetId, newName, duckdb) => {
          const state = get();
          const proj = state.project;
          if (!proj) {
            logger.warn('[ProjectStore] No project loaded');
            return;
          }

          const dataset = proj.datasets[datasetId];
          if (!dataset) {
            logger.warn('[ProjectStore] Dataset not found:', datasetId);
            return;
          }

          // 名前が変わらない場合は何もしない
          if (dataset.name === newName) {
            return;
          }

          // 重複チェック
          const existingDatasets = Object.entries(proj.datasets)
            .filter(([id]) => id !== datasetId);
          const duplicateName = existingDatasets.find(([_, d]) => d.name === newName);
          if (duplicateName) {
            throw new Error(`Dataset with name "${newName}" already exists (ID: ${duplicateName[0]})`);
          }

          logger.info('[ProjectStore] Updating dataset name with SQL dependency sync', {
            datasetId,
            oldName: dataset.name,
            newName
          });

          // 依存するSQL Query Operationを更新
          const updatedDatasets = await updateDependentSQLQueries(
            proj.datasets,
            dataset.name,
            newName,
            duckdb
          );

          // 対象データセットの名前を更新し、状態を反映
          set((state) => {
            if (!state.project) return state;

            const targetDataset = state.project.datasets[datasetId];
            if (!targetDataset) return state;

            const updatedTargetDataset = { ...targetDataset, name: newName };

            // DerivedDataSetのtransientマーカーを維持
            if (updatedTargetDataset.type === 'derived') {
              if (updatedTargetDataset.materialized) {
                clearTransient(updatedTargetDataset);
              } else {
                markAsTransient(updatedTargetDataset, ['data']);
              }
            }

            return {
              ...state,
              project: {
                ...state.project,
                datasets: {
                  ...updatedDatasets,
                  [datasetId]: updatedTargetDataset
                }
              }
            };
          });

          // プロジェクト更新通知
          get()._notifyProjectUpdate();
        },

        // データセットを削除
        removeDataset: (datasetId) => {
          logger.assert(datasetId != null && datasetId.length > 0, 'Dataset ID must not be empty');
          
          const state = get();
          const datasetToRemove = state.datasets[datasetId];
          
          if (!datasetToRemove) {
            logger.error('[ProjectStore] Cannot remove dataset - not found', {
              datasetId,
              availableDatasets: Object.keys(state.datasets)
            });
            return;
          }
          
          logger.info('[ProjectStore] Removing dataset', {
            datasetId,
            datasetName: datasetToRemove.name,
            remainingDatasetCount: Object.keys(state.datasets).length - 1
          });
          
          state.updateProject((proj) => {
            const newDatasets = { ...proj.datasets };
            delete newDatasets[datasetId];
            return {
              ...proj,
              datasets: newDatasets
            };
          });
        },
        
        // 派生データセットを追加
        addDerivedDataset: (dataset) => {
          logger.assert(dataset != null, 'Derived dataset must not be null');
          logger.assert(dataset.id != null && dataset.id.length > 0, 'Derived dataset ID must not be empty');
          logger.assert(dataset.parentDatasetIds != null && dataset.parentDatasetIds.length > 0, 'Parent dataset IDs must not be empty');

          logger.info('[ProjectStore] Adding derived dataset', {
            datasetId: dataset.id,
            datasetName: dataset.name,
            parentDatasetIds: dataset.parentDatasetIds,
            operationType: dataset.operation.type
          });

          // materializedプロパティに基づいてtransientマーカーを設定
          // materialized === true の場合: dataをMDSファイルに保存する（transientマーカーなし）
          // それ以外の場合: dataをMDSファイルに保存しない（transientマーカーあり）
          if (!dataset.materialized) {
            markAsTransient(dataset, ['data']);
          }

          // 通常のデータセットとして追加
          get().addDataset(dataset as DataSet);
        },
        
        // データセットを取得
        getDataset: (datasetId) => {
          const state = get();
          const dataset = state.datasets[datasetId];
          if (!dataset) {
            logger.warn('[ProjectStore] Dataset not found:', datasetId);
          }
          return dataset;
        },
        
        // 全データセットを取得
        getAllDatasets: () => {
          const state = get();
          return Object.values(state.datasets);
        },
        
        // コールバック設定
        setProjectUpdateCallback: (callback) => {
          projectUpdateCallback = callback;
        },
        
        // 内部メソッド：プロジェクト更新を通知
        _notifyProjectUpdate: () => {
          const project = get().project;
          if (project && projectUpdateCallback) {
            projectUpdateCallback(project);
          }
        },

        // ===== ワークスペース操作 =====

        // 現在のワークスペースを取得
        getCurrentWorkspace: () => {
          const project = get().project;
          if (!project) return null;
          return project.workspaces.find(w => w.id === project.activeWorkspaceId) ?? null;
        },

        // IDでワークスペースを取得
        getWorkspaceById: (workspaceId) => {
          const project = get().project;
          if (!project) return null;
          return project.workspaces.find(w => w.id === workspaceId) ?? null;
        },

        // ワークスペースを作成
        createWorkspace: (options) => {
          const newId = `workspace-${crypto.randomUUID()}` as WorkspaceId;
          const layoutState = options.layoutState || createDefaultLayout();

          get().updateProject((proj) => ({
            ...proj,
            workspaces: [
              ...proj.workspaces,
              {
                id: newId,
                name: options.name,
                layoutState,
                createdFromStyleId: options.styleId,
                creationContext: options.creationContext,
              }
            ]
          }));

          return newId;
        },

        // ワークスペースを削除
        deleteWorkspace: (workspaceId) => {
          const project = get().project;
          if (!project) return;

          const index = project.workspaces.findIndex(w => w.id === workspaceId);
          if (index === -1) return;

          get().updateProject((proj) => {
            const newWorkspaces = proj.workspaces.filter(w => w.id !== workspaceId);

            // 削除するワークスペースがアクティブな場合、別のワークスペースに切り替え
            let newActiveWorkspaceId = proj.activeWorkspaceId;
            if (proj.activeWorkspaceId === workspaceId && newWorkspaces.length > 0) {
              const newIndex = Math.min(index, newWorkspaces.length - 1);
              newActiveWorkspaceId = newWorkspaces[newIndex].id;
            }

            return {
              ...proj,
              workspaces: newWorkspaces,
              activeWorkspaceId: newActiveWorkspaceId
            };
          });
        },

        // ワークスペースをリネーム
        renameWorkspace: (workspaceId, newName) => {
          get().updateProject((proj) => ({
            ...proj,
            workspaces: proj.workspaces.map(w =>
              w.id === workspaceId ? { ...w, name: newName } : w
            )
          }));
        },

        // ワークスペースを切り替え
        switchWorkspace: (workspaceId) => {
          const project = get().project;
          if (!project) return;

          const workspace = project.workspaces.find(w => w.id === workspaceId);
          if (!workspace) return;

          logger.info('[ProjectStore] Switching workspace', { workspaceId, workspaceName: workspace.name });

          get().updateProject((proj) => ({
            ...proj,
            activeWorkspaceId: workspaceId
          }));
        },

        // ワークスペースを移動
        moveWorkspace: (workspaceId, direction) => {
          get().updateProject((proj) => {
            const index = proj.workspaces.findIndex(w => w.id === workspaceId);
            if (index === -1) return proj;

            const newIndex = direction === 'left' ? index - 1 : index + 1;
            if (newIndex < 0 || newIndex >= proj.workspaces.length) return proj;

            const newWorkspaces = [...proj.workspaces];
            [newWorkspaces[index], newWorkspaces[newIndex]] = [newWorkspaces[newIndex], newWorkspaces[index]];

            return { ...proj, workspaces: newWorkspaces };
          });
        },

        // ワークスペースのコンテキストをクリア
        clearWorkspaceContext: (workspaceId) => {
          get().updateProject((proj) => ({
            ...proj,
            workspaces: proj.workspaces.map(w =>
              w.id === workspaceId ? { ...w, creationContext: undefined } : w
            )
          }));
        },

        // ===== レイアウト操作 =====

        // 現在のレイアウト（派生状態）
        get currentLayout() {
          const workspace = get().getCurrentWorkspace();
          return workspace?.layoutState ?? createDefaultLayout();
        },

        // レイアウトを設定
        setLayout: (layout) => {
          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => ({
            ...proj,
            workspaces: proj.workspaces.map(w =>
              w.id === proj.activeWorkspaceId ? { ...w, layoutState: layout } : w
            )
          }));
        },

        // タブを追加
        addTab: (containerId, tab, options) => {
          logger.info('[ProjectStore] Adding tab:', tab.id, 'to container:', containerId);

          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];
            const container = findContainerInNode(workspace.layoutState.rootPane, containerId);
            if (!container) {
              logger.warn('[ProjectStore] Container not found:', containerId);
              return proj;
            }

            // シングルトンタブ
            const singletonTypes = ['help', 'project-overview', 'project-lineage'];
            const existingTabIndex = container.tabs.findIndex(t =>
              t.id === tab.id || (singletonTypes.includes(tab.type) && t.type === tab.type)
            );

            let newContainer: TabContainer;
            if (existingTabIndex >= 0) {
              newContainer = { ...container, activeTabId: container.tabs[existingTabIndex].id };
            } else {
              newContainer = {
                ...container,
                tabs: [...container.tabs, tab],
                activeTabId: tab.id
              };
            }

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.id === containerId) return newContainer;
              if (node.type === 'split') {
                return {
                  ...node,
                  leftChild: updateNode(node.leftChild),
                  rightChild: updateNode(node.rightChild)
                };
              }
              return node;
            };

            const newLayoutState: LayoutState = {
              rootPane: updateNode(workspace.layoutState.rootPane),
              activeContainerId: options?.activatePane !== false ? containerId : workspace.layoutState.activeContainerId
            };

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: newLayoutState } : w
              )
            };
          });
        },

        // タブを閉じる
        closeTab: (containerId, tabId) => {
          logger.info('[ProjectStore] Closing tab:', tabId, 'in container:', containerId);

          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];
            const container = findContainerInNode(workspace.layoutState.rootPane, containerId);
            if (!container) return proj;

            const tabIndex = container.tabs.findIndex(t => t.id === tabId);
            if (tabIndex === -1) return proj;

            const newTabs = container.tabs.filter(t => t.id !== tabId);
            let newActiveTabId = container.activeTabId;
            if (container.activeTabId === tabId) {
              const nextTab = container.tabs[tabIndex + 1] || container.tabs[tabIndex - 1];
              newActiveTabId = nextTab?.id || createTabId('');
            }

            const newContainer: TabContainer = { ...container, tabs: newTabs, activeTabId: newActiveTabId };

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.id === containerId) return newContainer;
              if (node.type === 'split') {
                return {
                  ...node,
                  leftChild: updateNode(node.leftChild),
                  rightChild: updateNode(node.rightChild)
                };
              }
              return node;
            };

            let newRootPane = updateNode(workspace.layoutState.rootPane);

            // 空のペインを自動削除
            if (newTabs.length === 0 && workspace.layoutState.rootPane.id !== containerId && workspace.layoutState.rootPane.type === 'split') {
              newRootPane = cleanupEmptyPane(newRootPane, containerId);
            }

            let newActiveContainerId = workspace.layoutState.activeContainerId;
            if (newActiveContainerId === containerId && newTabs.length === 0) {
              newActiveContainerId = findFirstContainerId(newRootPane);
            }

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { rootPane: newRootPane, activeContainerId: newActiveContainerId } } : w
              )
            };
          });
        },

        // タブをアクティブ化
        activateTab: (containerId, tabId) => {
          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];
            const container = findContainerInNode(workspace.layoutState.rootPane, containerId);
            if (!container) return proj;

            const newContainer: TabContainer = { ...container, activeTabId: createTabId(tabId) };

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.id === containerId) return newContainer;
              if (node.type === 'split') {
                return { ...node, leftChild: updateNode(node.leftChild), rightChild: updateNode(node.rightChild) };
              }
              return node;
            };

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { rootPane: updateNode(workspace.layoutState.rootPane), activeContainerId: containerId } } : w
              )
            };
          });
        },

        // タブ状態を更新
        updateTabState: (containerId, tabId, updater) => {
          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];
            const container = findContainerInNode(workspace.layoutState.rootPane, containerId);
            if (!container) return proj;

            const tabIndex = container.tabs.findIndex(t => t.id === tabId);
            if (tabIndex === -1) return proj;

            const newTabs = [...container.tabs];
            newTabs[tabIndex] = updater(newTabs[tabIndex]);
            const newContainer: TabContainer = { ...container, tabs: newTabs };

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.id === containerId) return newContainer;
              if (node.type === 'split') {
                return { ...node, leftChild: updateNode(node.leftChild), rightChild: updateNode(node.rightChild) };
              }
              return node;
            };

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { ...workspace.layoutState, rootPane: updateNode(workspace.layoutState.rootPane) } } : w
              )
            };
          });
        },

        // タブタイトルを更新
        updateTabTitle: (tabId, newTitle) => {
          const result = get().findTab(tabId);
          if (!result) {
            logger.warn('[ProjectStore] Tab not found for title update:', tabId);
            return;
          }
          logger.info('[ProjectStore] Updating tab title:', tabId, 'to:', newTitle);
          get().updateTabState(result.container.id, tabId, (tab) => ({ ...tab, title: newTitle }));
        },

        // タブを移動
        moveTab: (tabId, fromContainerId, toContainerId, targetIndex) => {
          logger.info('[ProjectStore] Moving tab:', tabId, 'from:', fromContainerId, 'to:', toContainerId);

          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];
            const fromContainer = findContainerInNode(workspace.layoutState.rootPane, fromContainerId);
            const toContainer = findContainerInNode(workspace.layoutState.rootPane, toContainerId);
            if (!fromContainer || !toContainer) return proj;

            const tabIndex = fromContainer.tabs.findIndex(t => t.id === tabId);
            if (tabIndex === -1) return proj;

            const tab = fromContainer.tabs[tabIndex];
            const newFromTabs = fromContainer.tabs.filter(t => t.id !== tabId);
            const newFromActiveTabId = fromContainer.activeTabId === tabId
              ? (newFromTabs[0]?.id || createTabId(''))
              : fromContainer.activeTabId;

            const insertIndex = targetIndex ?? toContainer.tabs.length;
            const newToTabs = [...toContainer.tabs];
            newToTabs.splice(insertIndex, 0, tab);

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.id === fromContainerId) {
                return { ...fromContainer, tabs: newFromTabs, activeTabId: newFromActiveTabId };
              }
              if (node.id === toContainerId) {
                return { ...toContainer, tabs: newToTabs, activeTabId: tab.id };
              }
              if (node.type === 'split') {
                return { ...node, leftChild: updateNode(node.leftChild), rightChild: updateNode(node.rightChild) };
              }
              return node;
            };

            let newRootPane = updateNode(workspace.layoutState.rootPane);

            // 移動元が空になった場合は自動削除
            if (newFromTabs.length === 0 && fromContainerId !== toContainerId &&
                workspace.layoutState.rootPane.id !== fromContainerId && workspace.layoutState.rootPane.type === 'split') {
              newRootPane = cleanupEmptyPane(newRootPane, fromContainerId);
            }

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { rootPane: newRootPane, activeContainerId: createPaneId(toContainerId) } } : w
              )
            };
          });
        },

        // ペイン内でタブを移動
        moveTabWithinPane: (containerId, fromIndex, toIndex) => {
          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];
            const container = findContainerInNode(workspace.layoutState.rootPane, containerId);
            if (!container || !container.tabs[fromIndex]) return proj;

            const newTabs = [...container.tabs];
            const [tab] = newTabs.splice(fromIndex, 1);
            newTabs.splice(toIndex, 0, tab);

            const newContainer: TabContainer = { ...container, tabs: newTabs };

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.id === containerId) return newContainer;
              if (node.type === 'split') {
                return { ...node, leftChild: updateNode(node.leftChild), rightChild: updateNode(node.rightChild) };
              }
              return node;
            };

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { ...workspace.layoutState, rootPane: updateNode(workspace.layoutState.rootPane) } } : w
              )
            };
          });
        },

        // ペインを分割
        splitPane: (targetPaneId, direction, position, newPane) => {
          logger.info('[ProjectStore] Splitting pane:', targetPaneId, direction, position);

          let newPaneId = '';
          const project = get().project;
          if (!project || !project.activeWorkspaceId) return newPaneId;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];

            const newContainer: TabContainer = newPane || {
              type: 'container',
              id: createPaneId(`pane-${crypto.randomUUID()}`),
              activeTabId: createTabId(''),
              tabs: []
            };
            newPaneId = newContainer.id;

            let newRootPane: PaneNode;

            if (workspace.layoutState.rootPane.id === targetPaneId) {
              const splitNode: SplitPane = {
                type: 'split',
                id: createPaneId(`split-${crypto.randomUUID()}`),
                direction,
                splitRatio: 0.5,
                leftChild: position === 'before' ? newContainer : workspace.layoutState.rootPane,
                rightChild: position === 'before' ? workspace.layoutState.rootPane : newContainer
              };
              newRootPane = splitNode;
            } else {
              const replaceNode = (node: PaneNode): PaneNode => {
                if (node.id === targetPaneId) {
                  return {
                    type: 'split',
                    id: createPaneId(`split-${crypto.randomUUID()}`),
                    direction,
                    splitRatio: 0.5,
                    leftChild: position === 'before' ? newContainer : node,
                    rightChild: position === 'before' ? node : newContainer
                  } as SplitPane;
                }
                if (node.type === 'split') {
                  return { ...node, leftChild: replaceNode(node.leftChild), rightChild: replaceNode(node.rightChild) };
                }
                return node;
              };
              newRootPane = replaceNode(workspace.layoutState.rootPane);
            }

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { ...workspace.layoutState, rootPane: newRootPane } } : w
              )
            };
          });

          return newPaneId;
        },

        // ペインを閉じる
        closePane: (paneId) => {
          logger.info('[ProjectStore] Closing pane:', paneId);

          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];

            if (workspace.layoutState.rootPane.id === paneId) {
              logger.warn('[ProjectStore] Cannot close root pane');
              return proj;
            }

            const newRootPane = cleanupEmptyPane(workspace.layoutState.rootPane, paneId);
            let newActiveContainerId = workspace.layoutState.activeContainerId;
            if (newActiveContainerId === paneId) {
              newActiveContainerId = findFirstContainerId(newRootPane);
            }

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { rootPane: newRootPane, activeContainerId: newActiveContainerId } } : w
              )
            };
          });
        },

        // 分割比率を更新
        updateSplitRatio: (splitPaneId, ratio) => {
          const project = get().project;
          if (!project || !project.activeWorkspaceId) return;

          get().updateProject((proj) => {
            const workspaceIndex = proj.workspaces.findIndex(w => w.id === proj.activeWorkspaceId);
            if (workspaceIndex === -1) return proj;

            const workspace = proj.workspaces[workspaceIndex];

            const updateNode = (node: PaneNode): PaneNode => {
              if (node.type === 'split' && node.id === splitPaneId) {
                return { ...node, splitRatio: ratio };
              }
              if (node.type === 'split') {
                return { ...node, leftChild: updateNode(node.leftChild), rightChild: updateNode(node.rightChild) };
              }
              return node;
            };

            return {
              ...proj,
              workspaces: proj.workspaces.map((w, i) =>
                i === workspaceIndex ? { ...w, layoutState: { ...workspace.layoutState, rootPane: updateNode(workspace.layoutState.rootPane) } } : w
              )
            };
          });
        },

        // コンテナを探す
        findContainer: (containerId) => {
          const workspace = get().getCurrentWorkspace();
          if (!workspace) return undefined;
          return findContainerInNode(workspace.layoutState.rootPane, containerId);
        },

        // タブを探す
        findTab: (tabId) => {
          const workspace = get().getCurrentWorkspace();
          if (!workspace) return undefined;
          return findTabInLayout(workspace.layoutState.rootPane, tabId);
        },

        // アクティブなコンテナを取得
        getActiveContainer: () => {
          const workspace = get().getCurrentWorkspace();
          if (!workspace?.layoutState.activeContainerId) return undefined;
          return findContainerInNode(workspace.layoutState.rootPane, workspace.layoutState.activeContainerId);
        },

        // ドロップゾーンの処理
        handleDropZone: (dropZone, draggedTabId, draggedFromPaneId) => {
          logger.info('[ProjectStore] Handling drop zone:', dropZone.type, 'for tab:', draggedTabId);

          const targetContainerId = dropZone.targetPaneId;

          if (dropZone.type.startsWith('split-')) {
            const splitType = dropZone.type;
            const direction = splitType === 'split-top' || splitType === 'split-bottom' ? 'vertical' : 'horizontal';
            const splitPosition = splitType === 'split-top' || splitType === 'split-left' ? 'before' : 'after';

            const tabInfo = get().findTab(draggedTabId);
            if (!tabInfo) return;

            const newPaneId = get().splitPane(targetContainerId, direction, splitPosition);
            get().moveTab(draggedTabId, draggedFromPaneId, newPaneId);
          } else if (dropZone.type === 'merge') {
            get().moveTab(draggedTabId, draggedFromPaneId, targetContainerId);
          }
        }
      }),
    { name: 'ProjectStore' }
  )
);

// E2Eテスト用: windowオブジェクトに公開
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__MIDAS_PROJECT_STORE__ = useProjectStore;
}